import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiError, AuthService, MessageService } from '../../../../../core';
import { InfiniteScrollManager } from '../../../../../shared/utils/infinite-scroll';
import { ClienteCarteiraBaixarModalComponent } from '../../../../cliente-carteira/baixar';
import { ClienteCarteiraPreBaixarModalComponent } from '../../../../cliente-carteira/prebaixar';
import { KPI_RAP } from '../../../../kpis';
import { Cliente } from '../../../../pessoa/cliente';
import { ClienteCentralCompra, ClienteCentralCompraFormModalComponent, ClienteCentralCompraHttpService, ClienteCentralCompraParticipante, ClienteCentralCompraParticipanteFormInsertModalComponent } from '../../../../pessoa/cliente/central-compra';
import { ClienteFormModalComponent } from '../../../../pessoa/cliente/form-modal';
import { ClienteNomePipe } from '../../../../pessoa/cliente/pipes';
import { pessoaCentralCompras } from '../../../../pessoa/tipo';
import { ClienteAnaliseMercadoFilter } from '../../../adapters';
import { CLIENTE_ANALISE_MERCADO_PERIODICIDADE_ANUAL } from '../../../domain/cliente-analise-mercado-periodicidade.enum';
import { ClienteAnaliseMercadoLancamentoFormModalComponent } from '../../../lancamento';
import { perspectivaRAP } from '../../../perspectivas';
import { ClienteAnaliseMercadoRAPClienteEventProps, ClienteAnaliseMercadoRAPClienteResponse, ClienteAnaliseMercadoRAPOrderBy, ClienteAnaliseMercadoRAPResponse, ClienteAnaliseMercadoRAPSaveRequest, ClienteAnaliseMercadoRAPSaveResponse } from '../../adapters';
import { ClienteAnaliseMercadoRAPHttpService } from '../../services';
import { ClienteAnaliseMercadoRAPTableComponent } from '../table';

interface Config {
    /** Search */
    isInitialized: boolean;
    /** Indicador se está carregando a primeira pesquisa */
    isLoading: boolean;
    /** Indicador se realizou a primeira pesquisa */
    isLoaded: boolean;
    /** Se é um usuário representante */
    isRepresentante: boolean;
}

@Component({
    selector: 'sgt-cliente-analise-mercado-rap-form-table',
    templateUrl: './cliente-analise-mercado-rap-form-table.component.html',
    styleUrls: ['./cliente-analise-mercado-rap-form-table.component.less'],
    providers: [
        ClienteNomePipe
    ]
})
export class ClienteAnaliseMercadoRAPFormTableComponent implements OnInit, OnDestroy {
    /** Configs do component */
    config: Config;

    // Infinite Scroll
    private searchLimitDefault = 20;
    private searchSubs$ = new Subscription();
    searchResponse: ClienteAnaliseMercadoRAPResponse;
    infiniteScrollManager: InfiniteScrollManager<ClienteAnaliseMercadoRAPClienteResponse>;
    dataSub$: Observable<ClienteAnaliseMercadoRAPClienteResponse[]>;
    doneSub$: Observable<boolean>;

    /** cliente atual que está sendo analisado */
    private currentCliente: ClienteAnaliseMercadoRAPClienteResponse;

    /** Se o form está em modo de revisão */
    @Input() revisaoMode: boolean;
    /** Filtro da view */
    @Input() filter: ClienteAnaliseMercadoFilter;

    @ViewChild('container', {static: true}) container: ElementRef;
    @ViewChild('table') table: ClienteAnaliseMercadoRAPTableComponent;
    @ViewChild('lancamentoFormModal') lancamentoFormModal: ClienteAnaliseMercadoLancamentoFormModalComponent;
    @ViewChild('clienteFormModal') clienteFormModal: ClienteFormModalComponent;
    @ViewChild('clienteCarteiraPreBaixarModal') clienteCarteiraPreBaixarModal: ClienteCarteiraPreBaixarModalComponent;
    @ViewChild('clienteCarteiraBaixarModal') clienteCarteiraBaixarModal: ClienteCarteiraBaixarModalComponent;
    @ViewChild('clienteCentralCompraFormModal') clienteCentralCompraFormModal: ClienteFormModalComponent;
    @ViewChild('centralCompraFormModal') centralCompraFormModal: ClienteCentralCompraFormModalComponent;
    @ViewChild('centralCompraParticipanteFormModal') centralCompraParticipanteFormModal: ClienteCentralCompraParticipanteFormInsertModalComponent;

    // KPIs
    KPI_RAP = KPI_RAP;

    constructor(private authService: AuthService,
                private messageService: MessageService,
                private clienteNomePipe: ClienteNomePipe,
                private clienteCentralCompraHttpService: ClienteCentralCompraHttpService,
                private clienteAnaliseMercadoRAPHttpService: ClienteAnaliseMercadoRAPHttpService) { }

    ngOnInit(): void {
        // Obtendo o limit de registros por página para o infinite scroll
        const limit = this.getInitSearchLimit();

        // Inicializa o gerenciador de scroll
        this.infiniteScrollManager = new InfiniteScrollManager<ClienteAnaliseMercadoRAPClienteResponse>([], limit);

        // Obtém os observables para usar com o async pipe
        this.dataSub$ = this.infiniteScrollManager.items$;
        this.doneSub$ = this.infiniteScrollManager.done$;

        this.createConfig();
    }

    private createConfig() {
        this.config = {
            isInitialized: false,
            isLoaded: false,
            isLoading: false,
            isRepresentante: this.authService.isRepresentante()
        }
    }

    /** Seta o limit do search calculando quantas rows em médias cabem dentro do container */
    private getInitSearchLimit() {
        // Obtendo o height do container
        const containerContentBodyHeight = jQuery(this.container.nativeElement).outerHeight(true);
        // Dividindo o height do container por 30 (30px é o tamanho médio das rows na table)
        const limit = Math.round(containerContentBodyHeight / 30);

        // Se o valor calculado do limit for maior que o limit default, consideramos o limit calculado, se não, consideramos o default
        return limit > this.searchLimitDefault ? limit : this.searchLimitDefault;
    }

    /** Remove os dados da pesquisa */
    private clear() {
        this.searchResponse = null;
    }

    /** Ao alterar o Order By da table */
    onOrderByChange(orderBy: ClienteAnaliseMercadoRAPOrderBy) {
        console.log('orderBy:', orderBy);
        this.load(this.filter, orderBy);
    }

    async load(filter: ClienteAnaliseMercadoFilter, orderBy: ClienteAnaliseMercadoRAPOrderBy) {
        try {
            // Pesquisa inicializada
            if (!this.config.isInitialized) {
                this.config.isInitialized = true;
            }

            // Resetando o table view
            this.table.reset();

            // Limpando dados
            this.clear();

            // Pesquisando
            this.config.isLoading = true;
            this.config.isLoaded = false;

            this.searchResponse = await this.search(filter, orderBy);
            this.infiniteScrollManager.reset(this.searchResponse.clientes ?? []);

            this.config.isLoading = false;
            this.config.isLoaded = true;
        } catch (error) {

        }
    }

    /** Realiza a consulta da Análise de Mercado RAP */
    private search(filter: ClienteAnaliseMercadoFilter, orderBy: ClienteAnaliseMercadoRAPOrderBy): Promise<ClienteAnaliseMercadoRAPResponse> {
        return new Promise<ClienteAnaliseMercadoRAPResponse>((resolve) => {
            this.searchSubs$?.unsubscribe();

            this.searchSubs$ = this.clienteAnaliseMercadoRAPHttpService.search(filter, orderBy, -1, -1)
                .pipe(
                    take(1)
                )
                .subscribe(
                    res => resolve(res),
                    (error: ApiError) => {
                        this.messageService.error(error.message, error.cause, error);
                        resolve(null);
                    }
                );
        });
    }

    /** Carrega e atualiza a Análise de Mercado RAP do cliente */
    private searchOneCliente(idCliente: number): Promise<ClienteAnaliseMercadoRAPClienteResponse> {
        return new Promise<ClienteAnaliseMercadoRAPClienteResponse>((resolve, reject) => {
            this.clienteAnaliseMercadoRAPHttpService.searchOneCliente(idCliente, this.filter)
                .pipe(
                    take(1)
                )
                .subscribe(
                    cliente => resolve(cliente),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Carrega e atualiza a Análise de Mercado RAP do cliente */
    private async updateOrRemoveCliente(idCliente: number) {
        // Carregando a análise do cliente
        let cliente = await this.searchOneCliente(idCliente);

        // Se encontrar o cliente, atualiza ele com os novos dados
        if (cliente) {
            this.searchResponse.updateCliente(cliente);
            this.infiniteScrollManager.updateItem(cliente, (a, b) => a.idCliente === b.idCliente);
            return;
        }

        // Se não encontrar, então significa que pode ter sido removido das Análises de Mercado RAP, por isso, tentamos carregá-lo no array de clientes das análises já carregadas aqui no front
        cliente = this.searchResponse.getCliente(idCliente);
        if (cliente) {
            this.searchResponse.removeCliente(idCliente);
            this.infiniteScrollManager.removeItem(cliente, (a, b) => a.idCliente === b.idCliente);
        }
    }

    /** Ao realizar o infinite scroll */
    onInfiniteScroll() {
        if (this.infiniteScrollManager.isDone) {
            return;
        }

        this.infiniteScrollManager.loadNextPage();
    }

    /** Ao alterar algum valor no lançamento da Análise de Mercado RAP de um cliente */
    async onChange(cliente: ClienteAnaliseMercadoRAPClienteEventProps) {
        try {
            if (!cliente) {
                return;
            }

            // Salvando lançamento
            const response = await this.save(cliente);
            if (response) {
                // Atualizando cliente com dados após salvar
                this.updateCliente(cliente.id, response);
            }

            this.searchResponse.recalcularTotais();
        } catch (error) {
            this.messageService.error(error?.message ?? error, `Erro ao tentar salvar a Análise de Mercado do cliente ${this.clienteNomePipe.transform(cliente.nome, cliente.nomeFantasia, cliente.codigoIntegracaoErp, cliente.tipoPessoa)}`, error);
        }
    }

    /** Salva a Análise de Mercado RAP do cliente */
    private save(cliente: ClienteAnaliseMercadoRAPClienteEventProps): Promise<ClienteAnaliseMercadoRAPSaveResponse> {
        return new Promise<ClienteAnaliseMercadoRAPSaveResponse>((resolve, reject) => {
            if (!cliente) {
                reject(new Error('Cliente não informado'));
                return;
            }

            const request = new ClienteAnaliseMercadoRAPSaveRequest({
                idCliente: cliente.id,
                ano: this.filter.ano,
                periodicidade: CLIENTE_ANALISE_MERCADO_PERIODICIDADE_ANUAL.id,
                valorPotencial: cliente.valorPotencial,
                valorPretendido: cliente.valorPretendido
            });
            this.clienteAnaliseMercadoRAPHttpService.save(request)
                .pipe(take(1))
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Atualiza o cliente com os dados retornados após salvar sua Análise de Mercado RAP */
    private updateCliente(idCliente: number, response: ClienteAnaliseMercadoRAPSaveResponse) {
        const cliente = this.searchResponse.getCliente(idCliente);
        if (!cliente) {
            return;
        }

        // Atualizando a Classificação RAP do cliente
        cliente.updateClassificacaoRAP(response.idClassificacaoRAP, response.nomeClassificacaoRAP);
    }

    /** Seta o cliente atual que está sendo analisado */
    private setCurrentCliente(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        this.currentCliente = cliente;
    }

    /** Abre a análise de mercado do cliente */
    async openAnaliseMercado(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        try {
            this.setCurrentCliente(cliente);

            // Abrindo modal da Análise de Mercado RAP
            this.lancamentoFormModal?.open(perspectivaRAP, cliente.idCliente, this.filter.ano, null, null, null, true, this.revisaoMode);
        } catch (error) {
            console.error(error);
        }
    }

    /** Após salvar o lançamento da análise de mercado */
    afterSaveLancamento() {
        try {
            // Atualizando o cliente
            this.updateOrRemoveCliente(this.currentCliente?.idCliente ?? null);
        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, `Erro ao tentar atualizar ou remover a Análise de Mercado do cliente ${this.clienteNomePipe.transform(this.currentCliente?.nomeCliente, this.currentCliente?.nomeFantasiaCliente, this.currentCliente?.codigoIntegracaoErpCliente, this.currentCliente?.tipoPessoa)}`, error);
        }
    }

    /** Abre o modal do cadastro do cliente */
    openCliente(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        this.setCurrentCliente(cliente);
        this.clienteFormModal?.edit(cliente?.idCliente);
    }

    /** Após editar o cliente */
    async afterEditCliente() {
        try {
            if (!this.currentCliente) {
                return;
            }
            // Atualizando o cliente
            this.updateOrRemoveCliente(this.currentCliente.idCliente);

            // Se for uma Central de Compras, carregamos e atualizamos seus participantes
            if (this.currentCliente.isCentralCompras)  {
                // Obtendo os participantes da central de compras
                const participantes = await this.getParticipantesCentralCompras(this.currentCliente.idCliente);
                // Atualizando ou removendo os participantes
                participantes?.forEach(participante => this.updateOrRemoveCliente(participante.id));
            }
        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, 'Erro após atualizar cliente', error);
        }
    }

    /** Abre o modal para pré-baixar o cliente */
    preBaixarCliente(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        this.setCurrentCliente(cliente);
        this.clienteCarteiraPreBaixarModal?.insert(cliente?.idCliente);
    }

    /** Após pré-baixar o cliente */
    afterPreBaixarCliente() {
        try {
            // Atualizando ou removendo o cliente
            this.updateOrRemoveCliente(this.currentCliente?.idCliente ?? null);
        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, `Erro ao tentar atualizar ou remover a Análise de Mercado do cliente ${this.clienteNomePipe.transform(this.currentCliente?.nomeCliente, this.currentCliente?.nomeFantasiaCliente, this.currentCliente?.codigoIntegracaoErpCliente, this.currentCliente?.tipoPessoa)}`, error);
        }
    }

    /** Abre o modal para baixar o cliente */
    baixarCliente(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        this.setCurrentCliente(cliente);
        this.clienteCarteiraBaixarModal?.insert(cliente?.idCliente, cliente?.motivoBaixaCliente);
    }

    /** Após baixar o cliente */
    afterBaixarCliente() {
        try {
            // Atualizando ou removendo o cliente
            this.updateOrRemoveCliente(this.currentCliente?.idCliente ?? null);
        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, `Erro ao tentar atualizar ou remover a Análise de Mercado do cliente ${this.clienteNomePipe.transform(this.currentCliente?.nomeCliente, this.currentCliente?.nomeFantasiaCliente, this.currentCliente?.codigoIntegracaoErpCliente, this.currentCliente?.tipoPessoa)}`, error);
        }
    }

    /** Abre o modal para inserir uma compras no cliente */
    insertCentralCompra() {
        this.clienteCentralCompraFormModal?.insert(null, pessoaCentralCompras.id);
    }

    /** Após inserir uma central de compras */
    async afterInsertCentralCompra(cliente: Cliente) {
        try {
            if (!cliente) {
                return;
            }

            // Carregando e adicionando a Análise de Mercado RAP da Central de Compras inserida
            const cli = await this.searchOneCliente(cliente.id);
            if (cli) {
                this.searchResponse.addCliente(cli);
                this.infiniteScrollManager.addItem(cli);
            }

            // Obtendo os participantes da central de compras
            const participantes = await this.getParticipantesCentralCompras(cliente.id);
            // Atualizando ou removendo os participantes
            participantes?.forEach(participante => this.updateOrRemoveCliente(participante.id));

        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, `Erro após adicionar o novo cliente ${this.clienteNomePipe.transform(this.currentCliente?.nomeCliente, this.currentCliente?.nomeFantasiaCliente, this.currentCliente?.codigoIntegracaoErpCliente, this.currentCliente?.tipoPessoa)}`, error);
        }
    }

    /** Abre o modal para atualizar a central de compras */
    editCentralCompra(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        if (!cliente) {
            return;
        }

        this.centralCompraFormModal?.edit(cliente.idCliente);
    }

    /** Após salvar uma central de compras */
    afterSaveCentralCompra(centralCompras: ClienteCentralCompra) {
        try {
            if (!centralCompras) {
                return;
            }

            // Pesquisando a central de compras
            this.updateOrRemoveCliente(centralCompras.id);
            // Pesquisando os participantes da central de compras
            centralCompras.participantes?.forEach(participante => this.updateOrRemoveCliente(participante.id));
        } catch (error) {
            this.messageService.error(`${error?.message ?? error}. Tente atualizar a página para visualizar suas alterações`, 'Erro após salvar a Central de Compras', error);
        }
    }

    /** Abre o modal para inserir um participante em uma compras no cliente */
    insertParticipanteCentralCompra(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        if (!cliente) {
            return;
        }

        const participante = new ClienteCentralCompraParticipante(cliente.idCliente, cliente.idPessoa, cliente.codigoIntegracaoErpCliente, cliente.cnpjCpfCliente, cliente.nomeCliente, cliente.nomeFantasiaCliente);
        this.centralCompraParticipanteFormModal?.insert(participante);
    }

    /** Carrega os participantes de uma central de compras */
    private getParticipantesCentralCompras(idCentralCompras: number): Promise<ClienteCentralCompraParticipante[]> {
        return new Promise<ClienteCentralCompraParticipante[]>((resolve, reject) => {
            this.clienteCentralCompraHttpService.load(idCentralCompras)
                .pipe(take(1))
                .subscribe(
                    centralCompras => resolve(centralCompras?.participantes ?? []),
                    (error: ApiError) => reject(error)
                );
        });
    }

    ngOnDestroy(): void {
        this.searchSubs$?.unsubscribe();
        this.clear();
    }
}
